import { differenceInDays } from 'date-fns'
import { Wallet } from 'lucide-react'
import { Suspense, useMemo } from 'react'
import { CreditAndExpiryDisplay } from '@/components/shared/credits-and-expiry/credit-and-expiry-display'
import { H3 } from '@/components/sv/typographies'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { gql } from '@/lib/graphql/gql'
import { useIdCompany, useSuspenseQuery } from '@/lib/hooks'
import { CreditsContentFallback } from '@/routes/billing/credits/fallback'
import type {
  BillingCreditsQuery,
  BillingCreditsQueryVariables,
} from '@/routes/billing/credits/index.types.gen'

const CREDITS_QUERY = gql`
  query BillingCredits($idCompany: String!) {
    company(id: $idCompany) {
      id
      sevallaBilling @live {
        id
        creditUsage {
          id
          remainingCredits
          expiresAt
        }
      }
    }
  }
`

const Content = () => {
  const { idCompany } = useIdCompany()

  const { data } = useSuspenseQuery<
    BillingCreditsQuery,
    BillingCreditsQueryVariables
  >(CREDITS_QUERY, {
    variables: { idCompany },
  })

  const creditUsage = data?.company?.sevallaBilling?.creditUsage

  const balance = useMemo(() => {
    const remaining = creditUsage?.remainingCredits ?? 0
    return Math.floor(remaining / 100)
  }, [creditUsage])

  const daysUntilCreditExpiry = useMemo(
    () =>
      creditUsage?.expiresAt
        ? differenceInDays(
            new Date(creditUsage.expiresAt).getTime(),
            new Date().getTime()
          )
        : null,
    [creditUsage]
  )

  const expiresAt = useMemo(() => {
    if (daysUntilCreditExpiry === null) {
      return null
    }
    return daysUntilCreditExpiry === 1
      ? '1 day'
      : `${daysUntilCreditExpiry} days`
  }, [daysUntilCreditExpiry])

  const hasCreditExpiry = daysUntilCreditExpiry !== null

  return (
    <CreditAndExpiryDisplay
      variant="default"
      forceAlign="start"
      showTitle={false}
      balance={balance}
      expiresAt={expiresAt}
      hasCreditExpiry={hasCreditExpiry}
    />
  )
}

export const Credits = () => {
  return (
    <Card className="bg-secondary p-0 gap-0">
      <CardHeader className="px-5 pt-3 pb-1">
        <CardTitle>
          <H3 className="flex items-center gap-2 text-sm text-muted-foreground">
            <Wallet className="size-4" /> Credits
          </H3>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0 flex-1">
        <Card className="p-0 border-x-0 border-b-0 shadow-none h-full justify-center">
          <CardContent className="py-6">
            <Suspense fallback={<CreditsContentFallback />}>
              <Content />
            </Suspense>
          </CardContent>
        </Card>
      </CardContent>
    </Card>
  )
}
